import { ChevronRight } from "@/components/ui/icons";
import { Image, Platform, Pressable, Text, View } from "react-native";

import type { components } from "@/api/generated/openapi";
import { GemMark } from "@/components/brand/GemMark";
import { PROVIDER_LOGOS, type ProviderLogo } from "@/components/brand/provider-logos";
import { MoneyFigure } from "@/components/ui/MoneyFigure";
import { formatMoney } from "@/lib/money/money";
import { cn } from "@/lib/ui/cn";
import { useThemeColors } from "@/theme/theme";

/** One source's current quote, as the card-detail contract serves it (TRO-775). */
export type SourceQuote = {
  source: string;
  price: string;
  observed_on: string;
  change_pct?: string | null;
};

/** A separate Misprint comparison; it never participates in source-price selection. */
export type MisprintComparison = components["schemas"]["MisprintObservationOut"];

/**
 * The Misprint card URL if it is one we are willing to open, else undefined.
 *
 * Shared with the scan queue (TRO-1158) so one validator guards every Misprint link in the app: the
 * URL is provider data, and a second copy of this would drift from the first.
 */
export function misprintLink(value: string | null): string | undefined {
  if (!value || value.trim() !== value) return undefined;
  for (const char of value) {
    if (char === "\\" || char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127) return undefined;
  }
  try {
    const url = new URL(value);
    return url.protocol === "https:" && ["misprint.com", "www.misprint.com"].includes(url.hostname)
        && !url.username && !url.password && (!url.port || url.port === "443")
      ? value
      : undefined;
  } catch {
    return undefined;
  }
}

/** How long ago Misprint last saw this ask. Shared with the scan queue's stack (TRO-1158). */
export function reportedLabel(reportedAt: string | null | undefined, now: Date): string {
  if (!reportedAt) return "";
  const minutes = Math.floor((now.getTime() - new Date(reportedAt).getTime()) / 60_000);
  if (!Number.isFinite(minutes)) return "";
  if (minutes < 1) return "Reported just now";
  if (minutes < 60) return `Reported ${minutes}m ago`;
  if (minutes < 1440) return `Reported ${Math.floor(minutes / 60)}h ago`;
  return `Reported ${Math.floor(minutes / 1440)}d ago`;
}

/**
 * The rows a scope shows, in order, fixed per scope and never derived from which are populated.
 *
 * A stack that hid its empty rows would reflow from card to card, and "TCGplayer has no price for
 * this" would look the same as "this card has no TCGplayer row". A missing source renders an em
 * dash in place.
 *
 * The graded set is not the raw set minus one. TCGplayer quotes no graded price, and
 * `source='trovie'` is a raw-market derivation, so neither belongs on a slab. The graded pair is
 * Blended beside PriceCharting, the row that always has a number. Blended covers only a few graded
 * scopes, the expensive chase slabs where a second opinion is worth having (TRO-703).
 */
const RAW_ROWS = ["tcgplayer", "justtcg", "trovie"] as const;
const GRADED_ROWS = ["justtcg", "pricecharting"] as const;

/**
 * The rows a sealed product shows. A sealed box is not a card at Raw (TRO-804).
 *
 * A box has no condition ladder and no grade, so it gets its own scope. `pricecharting` is in,
 * because a box is often a PriceCharting identity and that row carries its outbound link. `trovie`
 * is out, because a raw-market derivation means nothing for a box.
 *
 * Both ingestion paths file a sealed price at `condition IS NULL` ("Unopened" and "Sealed" map to
 * no ladder rung), while the Raw scope reads Near Mint, so a box shown on Raw has no price on any
 * row.
 */
const SEALED_ROWS = ["tcgplayer", "justtcg", "pricecharting"] as const;

/** The scope labels the server sends for the two non-graded shapes. Anything else is a grade. */
const RAW_SCOPE = "Raw";
const SEALED_SCOPE = "Sealed";

function rowsForScope(scope: string): readonly string[] {
  if (scope === SEALED_SCOPE) return SEALED_ROWS;
  return scope === RAW_SCOPE ? RAW_ROWS : GRADED_ROWS;
}

/** The source keys a scope shows, in order, with Misprint's comparison row where it has one. */
export function stackSources(scope: string, misprint: unknown): string[] {
  const rows = [...rowsForScope(scope)];
  if (misprint) rows.splice(rows[0] === "tcgplayer" ? 1 : 0, 0, "misprint");
  return rows;
}

/** What the app calls each source. JustTCG is "Blended Market" here and nowhere "Just". */
export const LABELS: Record<string, string> = {
  tcgplayer: "TCGplayer",
  misprint: "Misprint",
  justtcg: "Blended Market",
  trovie: "Trovie Market",
  pricecharting: "PriceCharting",
};

/**
 * Server `source` spellings that name the same provider as a row key (TRO-813).
 *
 * This is a rollout seam that must ship before the server changes. `price_observation` stores an
 * ingest path in `source` (`sku-mirror`, `tcgtracking` and `tcgplayer-graded->scrydex` all mean
 * TCGplayer), and renaming them to provider names is a single `UPDATE`. This component keys its
 * rows on that value client-side, so running the rename first blanks the TCGplayer row on every
 * installed build until it takes an OTA. The rename may run only once this has reached installed
 * builds, and these entries may be deleted only after that.
 *
 * The row keys above already use the post-rename names, so the last step is a pure deletion.
 */
const ROW_ALIASES: Record<string, string> = {
  "sku-mirror": "tcgplayer",
  tcgtracking: "tcgplayer",
  "tcgplayer-graded->scrydex": "tcgplayer",
  "pricecharting-graded": "pricecharting",
  "scrydex-sealed": "scrydex",
};

/**
 * Returns the row a server-sent `source` belongs to. Unknown spellings pass through unchanged, so a
 * new provider renders under its own name rather than landing on another provider's row.
 */
function rowKeyFor(source: string): string {
  return ROW_ALIASES[source] ?? source;
}

/**
 * Returns the newest quote for a source's provider, matching legacy aliases too.
 *
 * Older servers can return both the provider name and a legacy alias (TRO-829). On a tie in
 * `observed_on`, the quote under the provider name wins.
 */
export function quoteForSource(
  quotes: readonly SourceQuote[],
  source: string,
): SourceQuote | undefined {
  const provider = rowKeyFor(source);
  let newest: SourceQuote | undefined;
  for (const quote of quotes) {
    if (rowKeyFor(quote.source) !== provider) continue;
    if (
      !newest || quote.observed_on > newest.observed_on
      || (quote.observed_on === newest.observed_on && quote.source === provider)
    ) newest = quote;
  }
  return newest;
}

/** The em dash a source with no figure renders, not "$0.00", which is a real price. */
const NO_FIGURE = "—";

/**
 * The two mark heights, which differ on purpose.
 *
 * Our own glyphs are one- or two-stroke outlines in the row's ink and read cleanly at the label's
 * 12px. `BlendedMark` expresses the same value as Tailwind's `h-3`, since it is drawn in classes.
 *
 * A vendor's mark is filled, detailed, multi-color artwork that does not survive 12px: the white
 * bolt on TCGplayer's front card disappears into the blue. 16px is where that bolt resolves.
 */
const GLYPH_HEIGHT = 12;
const VENDOR_MARK_HEIGHT = 16;

/**
 * Two overlapping circles, the blended figure's mark. It is neutral rather than a vendor logo,
 * because "Blended Market" reconciles several providers and one of their marks would name the
 * wrong author.
 */
function BlendedMark({ color }: { color: string; }) {
  return (
    // Decorative: the "Blended Market" label beside it names the row. An image role with no name
    // was announced as an unlabelled graphic (axe `role-img-alt`).
    <View className="h-3 w-[18px] flex-row items-center" aria-hidden>
      <View
        className="h-3 w-3 rounded-full border"
        style={{ borderColor: color, opacity: 0.9 }}
      />
      <View
        className="-ml-1.5 h-3 w-3 rounded-full border"
        style={{ borderColor: color, opacity: 0.55 }}
      />
    </View>
  );
}

/**
 * A price provider's own mark (TRO-807).
 *
 * Width comes from the registry's `aspect`, so marks of different proportions read as the same
 * size when stacked. `contain` only guards an `aspect` that has drifted from the file, letterboxing
 * rather than stretching the logo.
 *
 * Unlike the other marks here it takes no `color` prop: these are multi-color artwork, which
 * `LOGOS.md` forbids re-inking, so they alone do not follow the theme.
 *
 * Hidden from screen readers because the row sets the provider's name in text beside it.
 */
/** Exported for the graded panel's attribution (TRO-1234) rather than copied, so one place
 *  decides a mark's draw height and derives its width from the registry's `aspect`. */
export function ProviderMark({ logo }: { logo: ProviderLogo; }) {
  return (
    <Image
      source={logo.source}
      style={{ height: VENDOR_MARK_HEIGHT, width: VENDOR_MARK_HEIGHT * logo.aspect }}
      resizeMode="contain"
      aria-hidden
    />
  );
}

/** A provider's lockup at `height`, read aloud as the provider's name it stands in for. */
export function ProviderLockup({ logo, height, label, tintColor }: {
  logo: ProviderLogo;
  height: number;
  label: string;
  /** Only for provider artwork published as a currentColor silhouette. */
  tintColor?: string;
}) {
  return (
    <Image
      source={logo.source}
      style={{ height, width: height * logo.aspect }}
      tintColor={tintColor}
      resizeMode="contain"
      accessibilityRole="image"
      accessibilityLabel={label}
    />
  );
}

export function SourceMark({ source, color }: { source: string; color: string; }) {
  if (source === "justtcg") return <BlendedMark color={color} />;
  if (source === "trovie") return <GemMark size={GLYPH_HEIGHT} color={color} />;
  // The branches above are our marks; this one is a vendor's. A provider absent from the registry
  // shows its name alone, which `LOGOS.md` prefers over a logo approximated from memory.
  const logo = PROVIDER_LOGOS[source];
  return logo ? <ProviderMark logo={logo} /> : null;
}

/** Returns a relative "Updated 3d ago" line, because the line exists to show staleness.
 *
 *  Exported for the scan queue's stack (TRO-1158) rather than copied: the calendar-day arithmetic
 *  below is the fix for TRO-1123, and a second copy would not carry it. */
export function updatedLabel(observedOn: string, now: Date): string {
  // `observed_on` is a calendar date, so the age is counted in the viewer's calendar days. Counting
  // hours from its UTC midnight called all of today's prices "yesterday" from 8 PM EDT (TRO-1123).
  // Both ends are built with `Date.UTC` so a 23- or 25-hour DST day still counts as one.
  const [year, month, day] = observedOn.split("-").map(Number);
  const then = Date.UTC(year, month - 1, day);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((today - then) / 86_400_000);
  if (Number.isNaN(days)) return "";
  if (days <= 0) return "Updated today";
  if (days === 1) return "Updated yesterday";
  if (days < 30) return `Updated ${days}d ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? "Updated 1mo ago" : `Updated ${months}mo ago`;
}

/** A quote's move as ▲ or ▼ and a percent, so the direction never rests on color alone. */
export function TrendChip({ changePct }: { changePct: string | null | undefined; }) {
  // Absent, not zero: `null` means no observation at the far end of the window to compare
  // against, and 0% would claim the price held steady.
  if (changePct == null) return null;
  const value = Number(changePct);
  if (!Number.isFinite(value)) return null;
  const up = value >= 0;
  return (
    <Text className={cn("font-num-bold text-[12px]", up ? "text-gain" : "text-loss")}>
      {up ? "▲" : "▼"} {Math.abs(value).toFixed(1)}%
    </Text>
  );
}

function PriceRow(
  { source, quote, misprint, href, onPress, now, last }: {
    source: string;
    quote?: SourceQuote;
    misprint?: MisprintComparison;
    href?: string;
    onPress?: () => void;
    now: Date;
    last: boolean;
  },
) {
  const c = useThemeColors();
  const label = LABELS[source] ?? source;
  // Only the lowest live ask, which is what a buyer actually pays on Misprint. Their market price
  // never stands in for it: for an ungraded or sealed scope it is TCGplayer-derived, which the row
  // above already shows, and for a graded scope it comes from a proprietary model the provider
  // describes as its least confident. A scope nobody is asking on reads as unpriced instead.
  const price = source === "misprint" ? misprint?.best_ask : quote?.price;
  const priced = price != null;
  const timestamp = source === "misprint"
    ? reportedLabel(misprint?.reported_at, now)
    : quote
    ? updatedLabel(quote.observed_on, now)
    : "";
  const description = `${label}, ${priced ? formatMoney(price) : "no price available"}.${
    timestamp ? ` ${timestamp}.` : ""
  }`;
  const body = (
    <View
      className={cn("min-h-11 justify-center py-2", last ? null : "border-b border-border/10")}
      accessible={source === "misprint" && !onPress}
      accessibilityLabel={source === "misprint" && !onPress ? description : undefined}
      testID={!onPress ? `price-source-${source}` : undefined}
    >
      <View className="flex-row items-center gap-1.5">
        <SourceMark source={source} color={c.mutedForeground} />
        <Text className="flex-1 font-label text-[12px] text-muted-foreground">{label}</Text>
        {onPress
          ? <ChevronRight aria-hidden size={14} color={c.subtle} style={{ opacity: 0.35 }} />
          : null}
      </View>
      <View className="mt-0.5 flex-row items-baseline gap-2">
        {priced
          ? <MoneyFigure value={formatMoney(price)} size="scoreboard" />
          : (
            <Text
              // Named so a harness rung can assert the absence of a figure on every surface; the
              // step vocabulary tests for an element, not for text.
              testID={`price-source-${source}-unpriced`}
              className="font-num-bold text-[20px] text-muted-foreground"
            >
              {NO_FIGURE}
            </Text>
          )}
        {source !== "misprint" && quote ? <TrendChip changePct={quote.change_pct} /> : null}
      </View>
      {timestamp
        ? <Text className="mt-0.5 font-body text-[10px] text-subtle">{timestamp}</Text>
        : null}
    </View>
  );

  // A verified product page remains useful when this grade has no price.
  if (!onPress) return body;
  return (
    <Pressable
      {...(Platform.OS === "web"
        ? { href, hrefAttrs: { target: "_blank", rel: "noopener noreferrer" } }
        : {})}
      onPress={(event) => {
        if (Platform.OS === "web") event.preventDefault();
        onPress();
      }}
      accessibilityRole="link"
      testID={`price-source-${source}`}
      accessibilityLabel={`${description} Open on ${label}`}
      className="active:opacity-70"
    >
      {body}
    </Pressable>
  );
}

/**
 * The card-detail price stack (TRO-775): what each source says this scope is worth, as rows of
 * equal weight.
 *
 * There is no hero price. One large number makes whichever provider answered look like the market;
 * the dealer's job is comparing sources, and a bigger figure on one row would make that comparison
 * for them.
 *
 * Valuation rows stay fixed per scope. A supported Misprint comparison adds its own row;
 * unavailable prices render an em dash.
 */
export function PriceStack(
  { quotes, scope, misprint, links, onOpen, now = new Date(), testID }: {
    quotes: SourceQuote[];
    misprint?: MisprintComparison | null;
    /**
     * The scope label the switcher is on. "Raw" and "Sealed" each select their own three-row set;
     * anything else selects the graded pair.
     */
    scope: string;
    /** Where each row goes when tapped, by source. A source with no entry renders unpressable. */
    links?: Partial<Record<string, string>>;
    /**
     * Opens a row's destination. Passed in so this component holds no navigation policy and a test
     * can assert what a row opens without a browser.
     */
    onOpen?: (url: string) => void;
    /** Injectable for tests, so "Updated 2d ago" is not a function of the wall clock. */
    now?: Date;
    testID?: string;
  },
) {
  const rows = stackSources(scope, misprint);
  return (
    <View testID={testID} className="flex-1">
      {rows.map((source, i) => {
        const href = source === "misprint"
          ? misprintLink(misprint?.card_url ?? null)
          : links?.[source];
        return (
          <PriceRow
            key={source}
            source={source}
            quote={quoteForSource(quotes, source)}
            misprint={source === "misprint" ? misprint ?? undefined : undefined}
            now={now}
            last={i === rows.length - 1}
            href={href}
            onPress={href && onOpen ? () => onOpen(href) : undefined}
          />
        );
      })}
    </View>
  );
}
